// Pure calculation for the Card vs Ozow pre-payment cost comparison.
//
// THREE-TIER AUTHORITATIVE-RATE MODEL (2026-10-12 correction):
//   - 'confirmed':  confirmedRates[method] is a real number — an
//     account-specific TradeSafe rate actually reconciled to the cent
//     against a REAL hosted-checkout total for THIS account (see
//     TRADESAFE_PROCESSING_FEE_PERCENT_CONFIRMED in backend/tradesafe_service.py).
//     This is the ONLY tier allowed to be treated as fully certified.
//   - 'documented': confirmedRates[method] is still null/absent but
//     documentedRates[method] is a real number — TradeSafe's own PUBLISHED
//     integration-rate documentation (3.0% Card / 1.5% Ozow, excluding VAT,
//     as of 2026-10-12 — see TRADESAFE_PROCESSING_RATE_DOCUMENTED). This is
//     a real, sourced number and IS shown/quantified, but it is NOT
//     certified: the known real R500 Card production test (TT-343663)
//     charged R530.41, and that has not yet been checked against this
//     formula to the cent for either method. Never silently promoted to
//     'confirmed' — only an explicit confirmedRates entry does that.
//   - 'unknown': neither exists — "Confirmed at secure checkout", no number
//     invented at all (the original, still-safe fallback).
//
// Fee formula for both 'confirmed' and 'documented' tiers — TradeSafe states
// its rate as "X% of transaction value, EXCLUDING VAT":
//   feeExclVat = base * (percent / 100)
//   fee        = feeExclVat * (1 + vatPercent / 100)
//   youPay     = base + fee
// (superseded 2026-10-17 — a conditional fixed charge is now added to
// feeExclVat; see the QUICK-SETTLEMENT comment above
// buildPaymentMethodFeeComparison.) This is the one place to change if a real hosted-checkout reconciliation
// (see the 'confirmed' tier) ever shows TradeSafe's real formula differs.
//
// RECONCILIATION CORRECTION (same day, PR #64 review): applying this exact
// formula to the known real R500 Card evidence (item 500 + TrustTrade fee 10
// = base 510; 3.0% + 15% VAT) produces R527.60 — NOT the R530.41 the real
// hosted checkout actually charged. The 'documented' tier's figure is
// therefore explicitly NOT presented as "You pay" / an exact charge — see
// youPayLabel/youPayCaveat below. Only the 'confirmed' tier (reconciled to
// the cent against a real transaction) may ever say "You pay".

export const PAYMENT_METHOD_LABELS = {
  CARD: 'Credit / Debit Card',
  OZOW: 'Ozow Instant EFT',
};

// Half-up to the cent. Cents are first trimmed to 12 significant digits so a
// binary-float artefact (23.345 -> 2334.4999999999995) still rounds up, like
// the backend's Decimal ROUND_HALF_UP.
function roundToCents(value) {
  return Math.round(Number((value * 100).toPrecision(12))) / 100;
}

function isRealNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

// QUICK-SETTLEMENT FIXED CHARGE + ESTIMATE GATE (2026-10-17 correction):
// observed live checkouts — Ozow R500 stage R524.55 (we showed R518.80),
// Card R500 R530.41 (we showed R527.60). TradeSafe's FAQ documents an extra
// R5 (excl. VAT) per payee party requiring quicker-than-month-end payout,
// per provider transaction; our seller tokens are always IMMEDIATE. So the
// documented formula is now:
//   feeExclVat = base * (percent / 100) + fixedFeeExclVat
//   fee        = feeExclVat * (1 + vatPercent / 100)
// where fixedFeeExclVat = R5 * charged_party_count (backend
// provider_quick_settlement_fee). A method's documented figure is only
// quantified when ALL of these hold, otherwise it falls back to 'unknown'
// ("Exact charges confirmed at secure checkout"):
//   - estimateReconciled[method] === true: the documented formula reproduced
//     an observed live total to the cent (Ozow yes; Card no — published 3%
//     does not reconcile and an assumed 2.5% is never substituted);
//   - fixedFeeExclVat is a real number (the conditional charge is known);
//   - estimateEligible === true: the caller's deal matches the observed
//     conditions (buyer pays all fees, no extra pass-through charges such as
//     courier). Defaults to false — fail closed.
// The confirmed tier also requires estimateEligible, and adds the fixed
// charge when one is supplied.
export function buildPaymentMethodFeeComparison({
  baseBeforeProvider,
  confirmedRates = {},
  documentedRates = {},
  estimateReconciled = {},
  fixedFeeExclVat = null,
  estimateEligible = false,
  vatPercent = 0,
  methods = ['CARD', 'OZOW'],
}) {
  const base = Number(baseBeforeProvider) || 0;
  const vat = Number(vatPercent) || 0;
  const fixedKnown = isRealNumber(fixedFeeExclVat);

  const rows = methods.map((method) => {
    const confirmedPercent = confirmedRates?.[method];
    const documentedPercent = documentedRates?.[method];
    const isConfirmed = estimateEligible === true && isRealNumber(confirmedPercent);
    const isDocumented = !isConfirmed
      && estimateEligible === true
      && isRealNumber(documentedPercent)
      && estimateReconciled?.[method] === true
      && fixedKnown;
    const tier = isConfirmed ? 'confirmed' : isDocumented ? 'documented' : 'unknown';

    if (tier === 'unknown') {
      return {
        method,
        label: PAYMENT_METHOD_LABELS[method] || method,
        tier,
        confirmed: false,
        providerFeePercent: null,
        providerFeeAmount: null,
        processingFeeAmount: null,
        settlementFeeAmount: null,
        settlementFeeExclVat: null,
        youPay: roundToCents(base),
        youPayLabel: 'Total before provider fee',
        youPayCaveat: null,
        cheaper: false,
      };
    }

    const percent = isConfirmed ? confirmedPercent : documentedPercent;
    const fixed = fixedKnown ? fixedFeeExclVat : 0;
    const vatFactor = 1 + vat / 100;
    const providerFeeAmount = roundToCents((base * (percent / 100) + fixed) * vatFactor);
    const processingFeeAmount = roundToCents(base * (percent / 100) * vatFactor);
    // Remainder, so the two displayed rows always sum to the fee exactly.
    const settlementFeeAmount = roundToCents(providerFeeAmount - processingFeeAmount);
    const youPay = roundToCents(base + providerFeeAmount);

    return {
      method,
      label: PAYMENT_METHOD_LABELS[method] || method,
      tier,
      confirmed: tier === 'confirmed',
      providerFeePercent: percent,
      providerFeeAmount,
      processingFeeAmount,
      settlementFeeAmount: fixed > 0 ? settlementFeeAmount : null,
      settlementFeeExclVat: fixed > 0 ? fixed : null,
      youPay,
      // Only the reconciled 'confirmed' tier may ever say "You pay" — the
      // 'documented' tier (published pricing that matched an observed
      // checkout, not a confirmed merchant rate) is explicitly an estimate.
      youPayLabel: tier === 'confirmed' ? 'You pay' : 'Published-rate estimate',
      youPayCaveat: tier === 'confirmed' ? null : 'Estimate only. Exact amount confirmed below in secure checkout.',
      cheaper: false,
    };
  });

  // "Lower processing fee" compares any two methods that are BOTH
  // quantified (confirmed OR documented) — an 'unknown' ("confirmed at
  // secure checkout") figure is never weighed against a real one, since
  // that would imply a false comparison.
  const quantifiedRows = rows.filter((row) => row.tier !== 'unknown');
  if (quantifiedRows.length > 1) {
    const min = Math.min(...quantifiedRows.map((row) => row.youPay));
    const cheapest = quantifiedRows.filter((row) => row.youPay === min);
    if (cheapest.length === 1) {
      cheapest[0].cheaper = true;
    }
  }

  return rows;
}
